/**
 * SKU Master — single source of truth for physical products.
 *
 * Phase 1: ShipBob-synced items in a data grid (search, sort, filter, column
 * toggles, CSV export via the grid toolbar), status badges, row expand showing
 * ShipBob channel products, and an on-demand ShipBob sync.
 * Later phases add Shopify mappings, manual entry, quotes, and NetSuite.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import RuleIcon from "@mui/icons-material/Rule";
import {
  DataGridPro,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid-pro";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import SkuMasterService, { FreightMethod, NetSuiteItemConflict, SkuItem, SkuShipbobProduct, UnmatchedVariant } from "services/SkuMasterService";
import InventoryService from "services/InventoryService";
import CashflowService from "services/CashflowService";
import { Supplier } from "models/CashflowModel";
import SupplierManagementDialog from "pages/Cashflow/components/dialogs/SupplierManagementDialog";
import PercentIcon from "@mui/icons-material/Percent";
import MappingReviewDialog from "./components/MappingReviewDialog";
import QuoteUploadDialog, { NewProductInput } from "./components/QuoteUploadDialog";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PackComponentsDialog from "./components/PackComponentsDialog";

const PRICE_COLUMNS: Array<{ method: FreightMethod; label: string }> = [
  { method: "40hc", label: "DDP 40'HQ" },
  { method: "40gp", label: "DDP 40'GP" },
  { method: "20dc", label: "DDP 20'DC" },
  { method: "lcl", label: "LCL" },
  { method: "fob", label: "FOB" },
];

type BadgeKey = "noMfrSku" | "noBarcode" | "noShopify" | "noQuote" | "noNetsuite" | "inBundle" | "pack" | "soldInPacks";

const BADGES: Record<BadgeKey, { label: string; color: "error" | "warning" | "info" | "default"; tooltip: string }> = {
  noMfrSku: { label: "no mfr SKU", color: "error", tooltip: "Missing Danican part number" },
  noBarcode: { label: "no barcode", color: "warning", tooltip: "No GS1 barcode in ShipBob" },
  noShopify: { label: "no Shopify", color: "warning", tooltip: "No Shopify variant mapped (phase 2)" },
  noQuote: { label: "no quote", color: "info", tooltip: "No confirmed supplier quote" },
  noNetsuite: { label: "no NetSuite", color: "default", tooltip: "No NetSuite item carries this ShipBob inventory id (as of the last sync)" },
  inBundle: { label: "in bundle", color: "info", tooltip: "Used as a component of a ShipBob bundle" },
  pack: { label: "multi-pack", color: "info", tooltip: "A pack of single items — the Inventory page counts it with them (see the Pack column)" },
  soldInPacks: { label: "sold in packs", color: "default", tooltip: "Also sold as part of a multi-pack; the Inventory page shows the family together" },
};

function badgesFor(item: SkuItem): BadgeKey[] {
  const badges: BadgeKey[] = [];
  if (!item.manufacturerSku) badges.push("noMfrSku");
  if (!item.barcode) badges.push("noBarcode");
  if (item.shopifyMappingCount === 0) badges.push("noShopify");
  if (item.quoteCounts.confirmed === 0) badges.push("noQuote");
  if (!item.netsuiteItemId) badges.push("noNetsuite");
  if (item.usedInBundles > 0) badges.push("inBundle");
  if (item.packComponents.length > 0) badges.push("pack");
  if (item.soldInPacks.length > 0) badges.push("soldInPacks");
  return badges;
}

function formatDims(l: number | null, w: number | null, h: number | null, unit: string | null): string {
  if (l == null || w == null || h == null) return "";
  return `${l}×${w}×${h} ${unit || ""}`.trim();
}

function DetailPanel({ item }: { item: SkuItem }): JSX.Element {
  return (
    <Box sx={{ p: 2, backgroundColor: "#F8FAFC" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>ShipBob products / channels</Typography>
      {item.shipbobProducts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No channel records synced.</Typography>
      ) : (
        <Paper variant="outlined" sx={{ maxWidth: 900, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product ID</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Reference ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.shipbobProducts.map((product: SkuShipbobProduct, index: number) => (
                <TableRow key={`${product.shipbobProductId}-${product.channelId ?? index}`}>
                  <TableCell>{product.shipbobProductId}</TableCell>
                  <TableCell>{product.sku}</TableCell>
                  <TableCell>{product.channelName || product.channelId || "—"}</TableCell>
                  <TableCell>{product.referenceId || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Shopify mappings</Typography>
      {item.shopifyMappings.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No Shopify variants mapped to this item.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ maxWidth: 900, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>Listing</TableCell>
                <TableCell>Shopify SKU</TableCell>
                <TableCell>Variant ID</TableCell>
                <TableCell>Matched by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.shopifyMappings.map(mapping => (
                <TableRow key={mapping.id}>
                  <TableCell><Chip label={mapping.store} size="small" variant="outlined" /></TableCell>
                  <TableCell>{mapping.title || "—"}</TableCell>
                  <TableCell>{mapping.shopifySku || "—"}</TableCell>
                  <TableCell>{mapping.shopifyVariantId}</TableCell>
                  <TableCell>{mapping.matchedBy || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      <Typography variant="caption" color="text.secondary">
        Quote history appears here in a later phase.
      </Typography>
    </Box>
  );
}

export default function SkuMasterPage(): JSX.Element {
  const service = useMemo(() => new SkuMasterService(), []);
  const inventoryService = useMemo(() => new InventoryService(), []);
  const [items, setItems] = useState<SkuItem[] | null>(null);
  const [bundles, setBundles] = useState<SkuShipbobProduct[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pickedStart, setPickedStart] = useState<string>(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [pickedEnd, setPickedEnd] = useState<string>(moment().format("YYYY-MM-DD"));
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isQuoteUploadOpen, setIsQuoteUploadOpen] = useState(false);
  const [unmatched, setUnmatched] = useState<UnmatchedVariant[] | null>(null);

  // Supplier default tariff rates (the same supplier manager the Cashflow page uses).
  // A product's Tariff % in the grid overrides its supplier's default on PO lines.
  const cashflowService = useMemo(() => new CashflowService(), []);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isSupplierDialogOpen, setIsSupplierDialogOpen] = useState(false);
  const [packBeingEdited, setPackBeingEdited] = useState<SkuItem | null>(null);
  const [isSavingSupplier, setIsSavingSupplier] = useState(false);
  useEffect(() => {
    cashflowService.getSuppliers().then(result => { if (result) setSuppliers(result.suppliers); });
  }, [cashflowService]);
  const handleCreateSupplier = async (data: any) => {
    setIsSavingSupplier(true);
    const result = await cashflowService.createSupplier(data);
    if (result) setSuppliers(result.suppliers);
    setIsSavingSupplier(false);
  };
  const handleUpdateSupplier = async (id: number, data: any) => {
    setIsSavingSupplier(true);
    const result = await cashflowService.updateSupplier(id, data);
    if (result) setSuppliers(result.suppliers);
    setIsSavingSupplier(false);
  };
  const handleDeleteSupplier = async (id: number) => {
    setIsSavingSupplier(true);
    const result = await cashflowService.deleteSupplier(id);
    if (result) setSuppliers(prev => prev.filter(s => s.id !== id));
    setIsSavingSupplier(false);
  };

  const fetchItems = useCallback(async () => {
    const response = await service.getItems({ pickedStart, pickedEnd });
    if (response) {
      setItems(response.items);
      setBundles(response.bundles.filter(bundle => bundle.isBundle));
    } else {
      setErrorMessage("Failed to load SKU master items");
    }
  }, [service, pickedStart, pickedEnd]);

  const fetchReview = useCallback(async () => {
    const response = await service.getShopifyReview();
    if (response) setUnmatched(response.unmatched);
  }, [service]);

  // Two NetSuite items on one ShipBob id: the PO push refuses those lines, and
  // this tool never writes NetSuite item records, so the fix is by hand there.
  const [netsuiteConflicts, setNetsuiteConflicts] = useState<NetSuiteItemConflict[]>([]);
  const fetchNetsuiteConflicts = useCallback(async () => {
    const response = await service.getNetSuiteConflicts();
    if (response) setNetsuiteConflicts(response.conflicts);
  }, [service]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  useEffect(() => {
    fetchNetsuiteConflicts();
  }, [fetchNetsuiteConflicts]);

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncMessage(null);
    try {
      const shipbob = await service.syncShipBob();
      if (!shipbob?.ok) {
        setErrorMessage("ShipBob sync failed — check the sync log");
        return;
      }
      const shopify = await service.syncShopify();
      if (!shopify?.ok) {
        setErrorMessage("Shopify mapping sync failed — check the sync log");
        return;
      }
      const netsuite = await service.syncNetSuite();
      if (!netsuite?.ok) {
        setErrorMessage("NetSuite item sync failed — check the sync log");
        return;
      }
      setSyncMessage(`ShipBob: ${JSON.stringify(shipbob.summary)} • Shopify: ${JSON.stringify(shopify.summary)} • NetSuite: ${JSON.stringify(netsuite.summary)}`);
      await Promise.all([fetchItems(), fetchReview(), fetchNetsuiteConflicts()]);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAssign = async (variant: UnmatchedVariant, itemId: number): Promise<boolean> => {
    const result = await service.assignShopifyMapping({
      store: variant.store,
      shopifyVariantId: variant.variantId,
      shopifyProductId: variant.productId,
      shopifySku: variant.sku,
      title: variant.title,
      itemId,
    });
    if (!result?.ok) {
      setErrorMessage(`Failed to map ${variant.title}`);
      return false;
    }
    setUnmatched(prev => (prev || []).filter(v => !(v.store === variant.store && v.variantId === variant.variantId)));
    await fetchItems();
    return true;
  };

  const handleIgnore = async (variant: UnmatchedVariant): Promise<boolean> => {
    const result = await service.ignoreShopifyVariant({
      store: variant.store,
      shopifyVariantId: variant.variantId,
      reason: "manual",
    });
    if (!result?.ok) {
      setErrorMessage(`Failed to ignore ${variant.title}`);
      return false;
    }
    setUnmatched(prev => (prev || []).filter(v => !(v.store === variant.store && v.variantId === variant.variantId)));
    return true;
  };

  const EDITABLE_FIELDS = ["manufacturerSku", "pcsPerCarton", "cartonLength", "cartonWidth", "cartonHeight", "cartonWeight", "manufacturer", "notes", "tariffRate"] as const;

  const handleRowUpdate = async (updatedRow: SkuItem, originalRow: SkuItem): Promise<SkuItem> => {
    const updates: Record<string, unknown> = {};
    for (const field of EDITABLE_FIELDS) {
      if (updatedRow[field] !== originalRow[field]) {
        updates[field] = updatedRow[field] ?? null;
      }
    }
    if (Object.keys(updates).length === 0) return originalRow;

    // Throws with the server's message on rejection (e.g. duplicate mfr SKU);
    // the grid then reverts the cell via onProcessRowUpdateError.
    await service.updateItem(updatedRow.shipbobInventoryId, updates);
    setItems(prev => (prev || []).map(item =>
      item.shipbobInventoryId === updatedRow.shipbobInventoryId ? updatedRow : item
    ));
    return updatedRow;
  };

  // Every SKU in the catalog (items + their sales-channel product rows) —
  // powers the new-product SKU builder's vocab + uniqueness check.
  const existingSkus = useMemo(() => {
    const skus = new Set<string>();
    for (const item of items || []) {
      if (item.shipbobSku) skus.add(item.shipbobSku);
      for (const product of item.shipbobProducts) {
        if (product.sku) skus.add(product.sku);
      }
    }
    return Array.from(skus);
  }, [items]);

  // Creates the product in ShipBob (via the same flow as the PO page), then
  // refetches so the grid shows it; returns a minimal item so the quote line
  // can select it immediately.
  const handleCreateProduct = async (product: NewProductInput): Promise<SkuItem | null> => {
    const created = await inventoryService.createProduct(product);
    if (!created) {
      setErrorMessage(`Failed to create ${product.sku} — the server or ShipBob rejected it`);
      return null;
    }
    if (!created.costRecorded) {
      // The product exists in ShipBob either way and cannot be deleted — never
      // present a cost failure as a creation failure or a retry duplicates it.
      setErrorMessage(
        `Created ${created.sku} (inventory ID ${created.inventoryId}), but its unit cost was NOT saved` +
        `${created.costError ? ` (${created.costError})` : ""}. Do not create it again — set its cost in SKU Costs on the Purchase Orders page.`
      );
    }
    fetchItems();
    return {
      shipbobInventoryId: Number(created.inventoryId),
      shipbobSku: created.sku,
      name: created.name,
      manufacturerSku: null,
      barcode: null,
      manufacturer: null,
      unitLength: null,
      unitWidth: null,
      unitHeight: null,
      unitDimUnit: null,
      unitWeight: null,
      unitWeightUnit: null,
      pcsPerCarton: null,
      cartonLength: null,
      cartonWidth: null,
      cartonHeight: null,
      cartonDimUnit: null,
      cartonWeight: null,
      cartonWeightUnit: null,
      netsuiteItemId: null,
      netsuiteSku: null,
      tariffRate: null,
      status: null,
      notes: null,
      fulfillableQty: 0,
      onhandQty: 0,
      pickedQty: 0,
      prices: {},
      shopifyMappings: [],
      lastSyncedAt: null,
      shipbobProducts: [],
      usedInBundles: 0,
      shopifyMappingCount: 0,
      quoteCounts: { total: 0, confirmed: 0 },
      packComponents: [],
      soldInPacks: [],
    };
  };

  const lastSyncedAt = useMemo(() => {
    const timestamps = (items || []).map(item => item.lastSyncedAt).filter(Boolean) as string[];
    return timestamps.length > 0 ? timestamps.sort().slice(-1)[0] : null;
  }, [items]);

  // Danican number (or SKU) per inventory id, for the Pack column's "2 × SBL-101"
  const itemLabelById = useMemo(
    () => new Map((items || []).map(item => [String(item.shipbobInventoryId), item.manufacturerSku || item.shipbobSku || String(item.shipbobInventoryId)])),
    [items],
  );

  const columns: GridColDef<SkuItem>[] = useMemo(() => [
    { field: "shipbobInventoryId", headerName: "Inventory ID", width: 110 },
    { field: "shipbobSku", headerName: "ShipBob SKU", width: 220 },
    { field: "name", headerName: "Name", width: 260 },
    { field: "manufacturerSku", headerName: "Mfr SKU (Danican)", width: 150, editable: true },
    { field: "netsuiteSku", headerName: "NetSuite SKU", width: 150 },
    {
      field: "tariffRate", headerName: "Tariff %", width: 110, type: "number", editable: true,
      description: "Tariff rate for this product, as a percent. Blank = the supplier's default rate applies on POs.",
      valueGetter: ({ row }) => (row.tariffRate != null ? Math.round(row.tariffRate * 10000) / 100 : null),
      valueSetter: ({ value, row }) => ({ ...row, tariffRate: value == null || value === "" ? null : Math.round(Number(value) * 100) / 10000 }),
      valueFormatter: ({ value }) => (value != null ? `${value}%` : ""),
    },
    {
      field: "pack", headerName: "Pack", width: 170, sortable: false,
      description: "What a multi-pack contains. Worked out from Danican numbers like SBL-101+101; click to set it by hand.",
      valueGetter: ({ row }) => row.packComponents.map(component => `${component.qty} × ${itemLabelById.get(component.inventoryId) ?? component.inventoryId}`).join(" + "),
      renderCell: ({ row, value }) => (
        <Tooltip title={value ? "Click to change what this pack contains" : "Click to mark this item as a multi-pack"}>
          <Box
            onClick={() => setPackBeingEdited(row)}
            sx={{ cursor: "pointer", width: "100%", color: value ? "text.primary" : "text.disabled", "&:hover": { textDecoration: "underline" } }}
          >
            {value || "—"}
          </Box>
        </Tooltip>
      ),
    },
    { field: "barcode", headerName: "Barcode", width: 140 },
    { field: "manufacturer", headerName: "Manufacturer", width: 120, editable: true },
    {
      field: "fulfillableQty", headerName: "Fulfillable", width: 100, type: "number",
      valueGetter: ({ row }) => row.fulfillableQty,
    },
    {
      field: "onhandQty", headerName: "On hand", width: 100, type: "number",
      valueGetter: ({ row }) => row.onhandQty,
    },
    {
      field: "pickedQty", headerName: "Picked", width: 100, type: "number",
      description: "Units on orders in the selected date range",
    },
    ...PRICE_COLUMNS.map(({ method, label }): GridColDef<SkuItem> => ({
      field: `price_${method}`,
      headerName: label,
      width: 105,
      type: "number",
      description: "Latest confirmed quote price — effective until superseded by a newer quote",
      valueGetter: ({ row }) => row.prices?.[method]?.unitPrice ?? null,
      renderCell: ({ row }) => {
        const price = row.prices?.[method];
        if (!price) return "";
        // A quote's stated validity ("good for 60 days") is a supplier hedge,
        // not a price expiry — pricing holds until a newer quote supersedes it.
        return (
          <Tooltip title={`Quoted ${price.quoteDate || "(no date)"}${price.validUntil ? ` — stated validity ${price.validUntil}, effective until superseded` : ""}`}>
            <span>${price.unitPrice.toFixed(2)}</span>
          </Tooltip>
        );
      },
    })),
    {
      field: "unitDims", headerName: "Unit dims", width: 130, sortable: false,
      valueGetter: ({ row }) => formatDims(row.unitLength, row.unitWidth, row.unitHeight, row.unitDimUnit),
    },
    {
      field: "unitWeightDisplay", headerName: "Unit weight", width: 110, sortable: false,
      valueGetter: ({ row }) => (row.unitWeight != null ? `${row.unitWeight} ${row.unitWeightUnit || ""}`.trim() : ""),
    },
    { field: "pcsPerCarton", headerName: "Pcs/carton", width: 100, type: "number", editable: true },
    { field: "cartonLength", headerName: "Carton L (cm)", width: 110, type: "number", editable: true },
    { field: "cartonWidth", headerName: "Carton W (cm)", width: 110, type: "number", editable: true },
    { field: "cartonHeight", headerName: "Carton H (cm)", width: 110, type: "number", editable: true },
    { field: "cartonWeight", headerName: "Carton wt (kg)", width: 115, type: "number", editable: true },
    { field: "notes", headerName: "Notes", width: 200, editable: true },
    { field: "netsuiteItemId", headerName: "NetSuite ID", width: 110 },
    { field: "status", headerName: "Status", width: 90 },
    {
      field: "badges", headerName: "Flags", width: 300, sortable: true,
      valueGetter: ({ row }) => badgesFor(row).map(key => BADGES[key].label).join(", "),
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {badgesFor(row).map(key => (
            <Tooltip key={key} title={BADGES[key].tooltip}>
              <Chip label={BADGES[key].label} color={BADGES[key].color} size="small" variant="outlined" />
            </Tooltip>
          ))}
        </Box>
      ),
    },
    {
      field: "lastSyncedAt", headerName: "Last synced", width: 150,
      valueGetter: ({ row }) => (row.lastSyncedAt ? moment(row.lastSyncedAt).format("MMM D, h:mm a") : ""),
    },
  ], [itemLabelById]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
    <Box sx={{ p: 3, width: "100%", minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box>
          <Typography variant="h5">SKU Master</Typography>
          <Typography variant="caption" color="text.secondary">
            {items ? `${items.length} items` : "Loading…"}
            {bundles.length > 0 && ` • ${bundles.length} ShipBob bundles (read-only)`}
            {lastSyncedAt && ` • last ShipBob sync ${moment(lastSyncedAt).fromNow()}`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <DatePicker
            label="Picked from"
            value={moment(pickedStart)}
            onChange={(value: any) => {
              if (value && value.isValid()) setPickedStart(value.format("YYYY-MM-DD"));
            }}
            slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
          />
          <DatePicker
            label="Picked to"
            value={moment(pickedEnd)}
            onChange={(value: any) => {
              if (value && value.isValid()) setPickedEnd(value.format("YYYY-MM-DD"));
            }}
            slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<RequestQuoteIcon />}
            onClick={() => setIsQuoteUploadOpen(true)}
          >
            Upload quotes
          </Button>
          <Tooltip title="Default tariff rate per supplier — the starting rate for its PO lines. Set a product's own rate in the Tariff % column to override it.">
            <Button
              variant="outlined"
              size="small"
              startIcon={<PercentIcon />}
              onClick={() => setIsSupplierDialogOpen(true)}
            >
              Supplier tariffs
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RuleIcon />}
            onClick={() => setIsReviewOpen(true)}
          >
            Review mappings{unmatched != null ? ` (${unmatched.length})` : ""}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SyncIcon />}
            disabled={isSyncing}
            onClick={handleSync}
          >
            {isSyncing ? "Syncing…" : "Sync ShipBob + Shopify + NetSuite"}
          </Button>
        </Box>
      </Box>

      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 1 }}>{errorMessage}</Alert>
      )}
      {syncMessage && (
        <Alert severity="success" onClose={() => setSyncMessage(null)} sx={{ mb: 1 }}>{syncMessage}</Alert>
      )}
      {netsuiteConflicts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <strong>{netsuiteConflicts.length} ShipBob inventory id{netsuiteConflicts.length === 1 ? " is" : "s are"} on more than one NetSuite item.</strong>{" "}
          POs with these lines can't be pushed until the ShipBob inventory id is cleared from all but one item in NetSuite
          (this tool never edits NetSuite items). Re-sync afterwards.
          <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
            {netsuiteConflicts.map(conflict => (
              <li key={conflict.inventoryId}>
                ShipBob {conflict.inventoryId}:{" "}
                {conflict.items.map((item, index) => (
                  <React.Fragment key={item.netsuiteItemId}>
                    {index > 0 && " · "}
                    <Link href={`https://9703688.app.netsuite.com/app/common/item/item.nl?id=${item.netsuiteItemId}`} target="_blank" rel="noopener">
                      {item.itemid} (#{item.netsuiteItemId})
                    </Link>
                    {item.displayName ? ` — ${item.displayName}` : ""}
                  </React.Fragment>
                ))}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: "calc(100vh - 170px)", minHeight: 400 }}>
        <DataGridPro
          rows={items || []}
          columns={columns}
          getRowId={(row) => row.shipbobInventoryId}
          loading={items === null}
          density="compact"
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true, csvOptions: { fileName: "sku-master" } } }}
          getDetailPanelContent={({ row }) => <DetailPanel item={row} />}
          getDetailPanelHeight={() => "auto" as const}
          disableRowSelectionOnClick
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={(error: Error) => setErrorMessage(error.message)}
        />
      </Paper>

      <SupplierManagementDialog
        isOpen={isSupplierDialogOpen}
        onClose={() => setIsSupplierDialogOpen(false)}
        suppliers={suppliers}
        onCreateSupplier={handleCreateSupplier}
        onUpdateSupplier={handleUpdateSupplier}
        onDeleteSupplier={handleDeleteSupplier}
        isSaving={isSavingSupplier}
      />
      <MappingReviewDialog
        open={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        unmatched={unmatched}
        items={items || []}
        onAssign={handleAssign}
        onIgnore={handleIgnore}
      />

      <PackComponentsDialog
        pack={packBeingEdited}
        items={items || []}
        service={service}
        onClose={() => setPackBeingEdited(null)}
        onSaved={() => { setSyncMessage("Pack contents saved"); fetchItems(); }}
      />

      <QuoteUploadDialog
        open={isQuoteUploadOpen}
        onClose={() => setIsQuoteUploadOpen(false)}
        service={service}
        items={items || []}
        existingSkus={existingSkus}
        onCreateProduct={handleCreateProduct}
        onSaved={(saved) => {
          setSyncMessage(`Saved ${saved} quote price rows`);
          fetchItems();
        }}
      />
    </Box>
    </LocalizationProvider>
  );
}
